import React, { memo, useEffect, useState } from "react";
import {
  Accordion,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import CurrencyInput from "react-currency-input";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import ptBR from "date-fns/locale/pt-BR";

import { Creators as NotificationsActions } from "../../store/ducks/notifications";

import { isScopes } from "../../services/Auth";
import { maskCPF } from "../../utils/CPF";
import { maskCNPJ } from "../../utils/CNPJ";
import { jsonToForm, formToJson } from "../../utils/Sinister";
import { isEmpty, ucFirstAll } from "../../utils/String";
import { isValidForm } from "../../utils/Form";
import { clearObject, mergeObject } from "../../utils/Object";
import DateUtil from "../../utils/Date";

import ClientService from "../../services/Client";
import ClientSchemaReduced from "../../services/Client/SchemaReduced";
import DriverService from "../../services/Driver";
import DriverSchemaReduced from "../../services/Driver/SchemaReduced";
import PatrimonyService from "../../services/Patrimony";
import PatrimonySchemaReduced from "../../services/Patrimony/SchemaReduced";
import SinisterService from "../../services/Sinister";
import SinisterSchema from "../../services/Sinister/Schema";

const ACTIVE_KEY_DETAIL_DATA = "1";
const ACTIVE_KEY_GENERAL_DATA = "0";

registerLocale("pt-BR", ptBR);

const Sinister = ({
  options,
  props: { currentUser },
  funcs: { addNotice }
}) => {
  const { t } = useTranslation();
  const clientService = new ClientService();
  const driverService = new DriverService();
  const patrimonyService = new PatrimonyService();
  const sinisterService = new SinisterService();

  const {
    sinister: __sinister = {},
    close: __close = () => {}
  } = options;

  const [sinister, setSinister] = useState(jsonToForm(mergeObject(JSON.parse(JSON.stringify(SinisterSchema)), __sinister)));

  useEffect(() => {
    setTimeout(() => {
      if(!isScopes({ currentUser, scopes: [
        "is:master",
        "read:sinisters:all"
      ], every: false })) {
        setClientSelectedOption({
          dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client),
          label: currentUser.client.type === "LEG" ? (`${currentUser.client.legal.socialReason} | CNPJ: ${maskCNPJ(currentUser.client.legal.cnpj)}`) : (`${currentUser.client.personal.name} | CPF: ${maskCPF(currentUser.client.personal.cpf)}`),
          value: currentUser.client.id
        });
        setSinister(prevState => ({
          ...prevState,
          client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client)
        }));
      }
    }, 1000);
  }, []); /* eslint-disable-line */

  const [activeKey, setActiveKey] = useState(ACTIVE_KEY_GENERAL_DATA);

  const [activeOptions] = useState([
    { value: true, label: t("Active") },
    { value: false, label: t("Inactive") }
  ]);

  const [guiltyOptions] = useState([
    { value: true, label: t("Title.Guilty.Active") },
    { value: false, label: t("Title.Guilty.Inactive") }
  ]);

  const [clientSelectedOption, setClientSelectedOption] = useState(sinister.client.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), sinister.client),
    label: sinister.client.type === "LEG" ? (`${sinister.client.legal.socialReason} | CNPJ: ${maskCNPJ(sinister.client.legal.cnpj)}`) : (`${sinister.client.personal.name} | CPF: ${maskCPF(sinister.client.personal.cpf)}`),
    value: sinister.client.id
  });

  const [driverSelectedOption, setDriverSelectedOption] = useState(sinister.driver.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(DriverSchemaReduced)), sinister.driver),
    label: sinister.driver.name,
    value: sinister.driver.id
  });

  const [patrimonySelectedOption, setPatrimonySelectedOption] = useState(sinister.patrimony.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(PatrimonySchemaReduced)), sinister.patrimony),
    label: sinister.patrimony.description,
    value: sinister.patrimony.id
  });

  const [running, setRunning] = useState(false);

  const buttonDisabled = () => {
    if (!running) return false;
    return true;
  };

  const clientLoadOptions = (e, c) => {
    clientLoadOptionsDebounce(e, c);
  };

  const clientLoadOptionsDebounce = debounce(async (e, c) => {
    const clients = await clientService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterClient(clients));
  }, 1000);

  const changeActiveKey = (e) => {
    if (activeKey === e) return setActiveKey("-1");
    setActiveKey(e);
  };

  const driverLoadOptions = (e, c) => {
    driverLoadOptionsDebounce(e, c);
  };

  const driverLoadOptionsDebounce = debounce(async (e, c) => {
    const drivers = await driverService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterDriver(drivers));
  }, 1000);

  const filterClient = (clients) => {
    return clients.map(e => {
      return {
        dataAux: e,
        label: e.type === "LEG" ? (`${e.legal.socialReason} | CNPJ: ${maskCNPJ(e.legal.cnpj)}`) : `${e.personal.name} | CPF: ${maskCPF(e.personal.cpf)}`,
        value: e.id
      };
    });
  };

  const filterDriver = (drivers) => {
    return drivers.map(e => {
      return {
        dataAux: e,
        label: e.name,
        value: e.id
      };
    });
  };

  const filterPatrimony = (patrimonies) => {
    return patrimonies.map(e => {
      return {
        dataAux: e,
        label: e.description,
        value: e.id
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!running) {
      setRunning(true);
      let { eventKey, message, valid } = isValidForm(e);
      if (!valid) {
        if (!message) {
          message = t("Error.Fields.Empty");
        }
        if (eventKey !== -1) {
          setActiveKey(eventKey);
        }
        addNotice({
          title: t("Title.Sinister"),
          content: message
        });
      }
      else {
        let response = false;
        if (isCreate()) {
          response = await sinisterService.create({
            data: handleSubmitForm({ sinister }),
            token: currentUser.token
          });
        }
        else {
          response = await sinisterService.update({
            id: sinister.id,
            data: handleSubmitForm({ sinister }),
            token: currentUser.token
          });
        }

        if (response.success) {
          addNotice({
            title: t("Title.Sinister"),
            content: isCreate() ? t("Success.Insert") : t("Success.Update")
          });
          setTimeout(() => {
            __close();
          }, 200);
        }
        else {
          addNotice({
            title: t("Title.Sinister"),
            content: t(`Error.Sinister.${response.error.type}.${ucFirstAll(response.error.details.path)}`)
          });
        }
      }
    }
    setRunning(false);
  };

  const handleSubmitForm = ({ sinister }) => {
    let newSinister = Object.assign({}, JSON.parse(JSON.stringify(sinister)));
    newSinister = clearObject({ data: newSinister });
    newSinister.id = undefined;
    return formToJson(newSinister);
  };

  const inputDisabled = () => {
    if (!running) return false;
    return true;
  };

  const isCreate = () => {
    return sinister.id === "";
  };

  const patrimonyLoadOptions = (e, c) => {
    patrimonyLoadOptionsDebounce(e, c);
  };

  const patrimonyLoadOptionsDebounce = debounce(async (e, c) => {
    const patrimonies = await patrimonyService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterPatrimony(patrimonies));
  }, 1000);

  return (
    <Form id="forms-sinister" noValidate onSubmit={handleSubmit}>
      <Accordion defaultActiveKey={ACTIVE_KEY_GENERAL_DATA} activeKey={activeKey}>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_GENERAL_DATA} onClick={e => changeActiveKey(ACTIVE_KEY_GENERAL_DATA)}>{t("Title.GeneralData")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_GENERAL_DATA}>
            <Card.Body>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-sinister-active">
                    <Form.Label>{t("Label.Active")}:</Form.Label>
                    <Select
                      className="menu-outer-bottom"
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      isDisabled={inputDisabled()}
                      onChange={e => {
                        const active = e.value;
                        setSinister(prevState => ({
                          ...prevState,
                          active
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      options={activeOptions}
                      value={activeOptions.find(x => x.value === sinister.active)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-sinister-client">
                    <Form.Label>{t("Title.Client")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-bottom ${!isEmpty(sinister.client.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled() || !isScopes({
                        currentUser,
                        scopes: [
                          "is:master",
                          "read:sinisters:all"
                        ],
                        every: false
                      })}
                      loadOptions={clientLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if(e === null) {
                          setClientSelectedOption(e);
                          setSinister(prevState => ({
                            ...prevState,
                            client: JSON.parse(JSON.stringify(ClientSchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setClientSelectedOption({ dataAux, label, value });
                          setSinister(prevState => ({
                            ...prevState,
                            client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      placeholder={t("Title.Client.Select")}
                      value={clientSelectedOption}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(sinister.client.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Client")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-sinister-driver">
                    <Form.Label>{t("Title.Driver")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-bottom ${!isEmpty(sinister.driver.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled()}
                      loadOptions={driverLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if (e === null) {
                          setDriverSelectedOption(e);
                          setSinister(prevState => ({
                            ...prevState,
                            driver: JSON.parse(JSON.stringify(DriverSchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setDriverSelectedOption({ dataAux, label, value });
                          setSinister(prevState => ({
                            ...prevState,
                            driver: mergeObject(JSON.parse(JSON.stringify(DriverSchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      dataeventkey={ACTIVE_KEY_GENERAL_DATA}
                      placeholder={t("Title.Driver.Select")}
                      value={driverSelectedOption}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(sinister.driver.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Driver")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-sinister-patrimony">
                    <Form.Label>{t("Title.Patrimony")}:</Form.Label>
                    <AsyncSelect
                      className={`menu-outer-bottom ${!isEmpty(sinister.patrimony.id) ? "is-valid" : "is-invalid"}`}
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      cacheOptions
                      defaultOptions
                      isDisabled={inputDisabled()}
                      loadOptions={patrimonyLoadOptions}
                      loadingMessage={() => t("React.Select.Wait")}
                      noOptionsMessage={() => t("React.Select.NoOptions")}
                      onChange={e => {
                        if(e === null) {
                          setPatrimonySelectedOption(e);
                          setSinister(prevState => ({
                            ...prevState,
                            patrimony: JSON.parse(JSON.stringify(PatrimonySchemaReduced))
                          }));
                        }
                        else {
                          const { dataAux, label, value } = e;
                          setPatrimonySelectedOption({ dataAux, label, value });
                          setSinister(prevState => ({
                            ...prevState,
                            patrimony: mergeObject(JSON.parse(JSON.stringify(PatrimonySchemaReduced)), dataAux)
                          }));
                        }
                      }}
                      placeholder={t("Title.Patrimony.Select")}
                      value={patrimonySelectedOption}
                      required={true}
                    />
                    <Form.Control.Feedback className={`${!isEmpty(sinister.patrimony.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Patrimony")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Accordion.Toggle as={Card.Header} eventKey={ACTIVE_KEY_DETAIL_DATA} onClick={e => changeActiveKey(ACTIVE_KEY_DETAIL_DATA)}>{t("Title.DetailData")}</Accordion.Toggle>
          <Accordion.Collapse eventKey={ACTIVE_KEY_DETAIL_DATA}>
            <Card.Body>
              <Row>
                <Col xs={12}>
                  <div className="filter-date-fields">
                    <Form.Group controlId="forms-sinister-start-at">
                      <Form.Label>{t("Title.Date")}:</Form.Label>
                      <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                          <InputGroup.Text><i className="fas fa-calendar-minus" /></InputGroup.Text>
                        </InputGroup.Prepend>
                        <DatePicker
                          disabled={inputDisabled()}
                          selected={DateUtil.toDate({ date: sinister.date })}
                          onChange={dateStart => {
                            const todayDate = `${(new Date()).getFullYear()}-${(new Date()).getMonth()+1}-${(new Date()).getDate()}`;
                            const date = dateStart === null ? new Date(`${todayDate} 00:00:00`) : dateStart;
                            setSinister(prevState => ({
                              ...prevState,
                              date
                            }));
                          }}
                          selectsStart
                          startDate={DateUtil.toDate({ date: sinister.date })}
                          timeFormat="HH:mm"
                          timeIntervals={5}
                          showTimeSelect
                          showMonthDropdown
                          showYearDropdown
                          dateFormat="dd/MM/yyyy HH:mm:ss"
                          className="react-datepicker-form-control"
                          id="forms-sinister-start-at"
                          popperPlacement="top-center"
                          popperModifiers={{
                            offset: {
                              enabled: true,
                              offset: "5px, 10px"
                            },
                            preventOverflow: {
                              enabled: true,
                              escapeWithReference: false,
                              boundariesElement: "viewport"
                            }
                          }}
                          locale="pt-BR"
                          dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                        />
                      </InputGroup>
                    </Form.Group>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-sinister-guilty">
                    <Form.Label>{t("Label.Guilty")}:</Form.Label>
                    <Select
                      className="menu-outer-bottom"
                      classNamePrefix="select"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      isDisabled={inputDisabled()}
                      onChange={e => {
                        const guilty = e.value;
                        setSinister(prevState => ({
                          ...prevState,
                          guilty
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      options={guiltyOptions}
                      value={guiltyOptions.find(x => x.value === sinister.guilty)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-sinister-price">
                    <Form.Label>{t("Label.Price")}:</Form.Label>
                    <CurrencyInput
                      id="forms-sinister-price"
                      className={`form-control ${!isEmpty(sinister.price) ? "is-valid" : "is-invalid"}`}
                      type="text"
                      placeholder={t("Placeholder.Price")}
                      disabled={inputDisabled()}
                      decimalSeparator=","
                      thousandSeparator="."
                      precision="2"
                      prefix="R$ "
                      value={sinister.price}
                      onChangeEvent={e => {
                        const price = e.target.value;
                        setSinister(prevState => ({
                          ...prevState,
                          price
                        }));
                      }}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Price")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-sinister-address">
                    <Form.Label>{t("Label.Address")}:</Form.Label>
                    <Form.Control
                      as="textarea"
                      placeholder={t("Label.Address")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const address = e.target.value;
                        setSinister(prevState => ({
                          ...prevState,
                          address
                        }));
                      }}
                      autoComplete="off"
                      value={sinister.address}
                      isValid={!isEmpty(sinister.address)}
                      isInvalid={isEmpty(sinister.address)}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Address")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="forms-sinister-description">
                    <Form.Label>{t("Label.Description")}:</Form.Label>
                    <Form.Control
                      as="textarea"
                      placeholder={t("Label.Description")}
                      disabled={inputDisabled()}
                      onChange={e => {
                        const description = e.target.value;
                        setSinister(prevState => ({
                          ...prevState,
                          description
                        }));
                      }}
                      autoComplete="off"
                      value={sinister.description}
                      isValid={!isEmpty(sinister.description)}
                      isInvalid={isEmpty(sinister.description)}
                      dataeventkey={ACTIVE_KEY_DETAIL_DATA}
                      required={true}
                    />
                    <Form.Control.Feedback type="invalid">{t("Feedback.Description")}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
      <Form.Group className="default-form-button">
        <Button
          variant="dark"
          type="submit"
          disabled={buttonDisabled()}
        >
          {running ? <Spinner animation="border" size="sm" /> : t("Button.Save")}
        </Button>
      </Form.Group>
    </Form>
  );
};

const mapStateToProps = state => ({
  props: {
    currentUser: state.users.currentUser
  }
});

const mapDispatchToProps = dispatch => ({
  funcs: bindActionCreators(Object.assign({}, NotificationsActions), dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(Sinister));
